import * as React from "react";
import type { Agent } from "@/data/types";
import { Reveal, Overline } from "./primitives";
import {
  APPROVED_ALAIN_PROFILE,
  withApprovedAgentCredentials,
} from "@/data/approvedAgentProfile";

interface Props {
  agent: Agent;
}

/** Static About Alain section — do not AI-generate this copy. */
export const AboutAlain: React.FC<Props> = ({ agent }) => {
  const profile = withApprovedAgentCredentials(agent);
  const photo = profile.photoUrl || APPROVED_ALAIN_PROFILE.photoUrl;
  const team = profile.team || APPROVED_ALAIN_PROFILE.team;

  return (
    <section
      className="w-full bg-ivory-alt py-20 md:py-28"
      data-testid="about-alain"
      aria-label="About Alain Onesti"
    >
      <div className="mx-auto w-full max-w-3xl px-6 md:px-10">
        <Reveal>
          <Overline>About</Overline>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-charcoal md:text-4xl lg:text-5xl">
            A more analytical approach to selling real estate.
          </h2>
        </Reveal>

        <Reveal delay={0.06}>
          <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-start">
            {photo && (
              <img
                src={photo}
                alt={profile.name || APPROVED_ALAIN_PROFILE.name}
                loading="eager"
                decoding="async"
                className="h-28 w-28 shrink-0 rounded-full object-cover object-top ring-1 ring-charcoal/10 md:h-32 md:w-32"
                data-testid="about-alain-photo"
              />
            )}
            <div className="space-y-5 text-base leading-relaxed text-charcoal-soft md:text-lg">
              <p>
                I’m Alain Onesti, a Licensed Real Estate Salesperson with the{" "}
                {team} at Douglas Elliman.
              </p>
              <p>
                Before real estate, my career was built around technology and digital
                transformation. I bring the same mindset to selling property: study the
                information, identify what can be improved, build a clear strategy and
                execute deliberately.
              </p>
              <p>
                I don’t believe every unsold listing needs the same solution — and I don’t
                believe the first recommendation should automatically be a lower price.
              </p>
              <p>Every property deserves to be understood before it is repositioned.</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 border-t border-charcoal/10 pt-8">
            <p className="signature text-2xl text-charcoal md:text-3xl">Alain Onesti</p>
            <p className="mt-2 text-sm text-charcoal-soft">
              Licensed Real Estate Salesperson
            </p>
            <p className="mt-1 text-sm text-charcoal-muted">{team}</p>
            <p className="mt-1 text-sm text-charcoal-muted">Douglas Elliman</p>
            <p className="mt-1 text-sm text-charcoal-muted">
              575 Madison Avenue
              <br />
              New York, NY 10022
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
